
import { Link, useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { useState } from 'react';

import { Button, Callout, Field, inputClass } from '@/components/ui';
import { DEFAULT_NEXT, internalNext } from '@/lib/next-path';
import { DEMO_ADMIN_EMAIL } from '@/lib/session';
import { resetIdentity } from '@/query-client';
import { AFTER_SIGN_IN } from '@/storefront';

/**
 * Sign in. FR-01: email and password, plus magic link.
 *
 * The password field is present and labelled because that is the shipped flow,
 * but with no Supabase project attached there is nothing to check it against,
 * and the form says so plainly rather than implying a credential check that is
 * not happening.
 */
export function SignInForm({ supabaseReady }: { supabaseReady: boolean }) {
  const navigate = useNavigate();
  const router = useRouter();
  // `next` is whatever the admin gate bounced us off, so it is a runtime string
  // rather than one of the route tree's literal paths. TanStack types `to`
  // against the tree, which is the point of it. This is the one place that
  // cannot satisfy that, so it is cast here and nowhere else.
  const search = useSearch({ strict: false }) as { next?: string };
  /**
   * Two rules, and both are needed.
   *
   * `internalNext` refuses anything that is not a path on this site, because it
   * arrived in a query string and an open redirect off a sign-in form is how a
   * password ends up typed into somebody else's page.
   *
   * Its own fallback is `/account`, which is a STOREFRONT route. With the store
   * closed that redirects back to this form, so a correct password would look
   * like a failed sign-in. `AFTER_SIGN_IN` is the fallback that knows which.
   */
  const checked = internalNext(search.next);
  const next = checked === DEFAULT_NEXT ? AFTER_SIGN_IN : checked;

  const [errors, setErrors] = useState<{ email?: string; form?: string }>({});
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const email = String(form.get('email') ?? '').trim();
    // The field was collected and thrown away. With Supabase connected the
    // server needs it, and without it there was no way to sign in at all
    // (R-142).
    const password = String(form.get('password') ?? '');

    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setErrors({ email: 'Use an email address we can reply to.' });
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      const res = await fetch('/api/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(password ? { email, password } : { email }),
      });
      if (!res.ok) {
        // Say what the server said. "That sign in did not go through" for a
        // wrong password sends somebody to check their connection.
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } }
          | null;
        setErrors({ form: body?.error?.message ?? 'That sign in did not go through.' });
        return;
      }
      // Order matters: drop the signed-out cache before navigating, or the
      // admin gate reads a stale `me` and bounces us back here.
      resetIdentity();
      await router.invalidate();
      await navigate({ to: next as never });
    } catch {
      setErrors({ form: 'That sign in did not go through. Check your connection.' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-6">
      {errors.form && <Callout tone="danger" title={errors.form} />}

      {!supabaseReady && (
        <Callout tone="warn" title="Auth is not connected yet">
          Supabase Auth is not configured, so any address signs you in for the walkthrough.
          Use <code>{DEMO_ADMIN_EMAIL}</code> to see the admin surfaces.
        </Callout>
      )}

      <Field label="Email" htmlFor="email" error={errors.email}>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          className={inputClass}
          aria-invalid={Boolean(errors.email)}
        />
      </Field>

      <Field label="Password" htmlFor="password">
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          className={inputClass}
        />
      </Field>

      <Button type="submit" size="lg" disabled={submitting}>
        {submitting ? 'Signing in' : 'Sign in'}
      </Button>

      <p className="text-[15px] text-muted">
        {/* This said "Ask the studio to send you a reset", which was honest and
            was not a feature. It is one now. The magic link is still not built:
            nothing calls `signInWithOtp`, so nothing here offers it. T025c. */}
        <Link to="/auth/forgot" className="font-semibold underline">
          Forgotten your password?
        </Link>
      </p>
    </form>
  );
}
